import * as React from 'react';
import Dragger from 'antd/lib/upload/Dragger';
import { Button, message, Upload } from 'antd';
import { resolveApiPath } from 'client/core/appConfig';
import { useCallback, useState } from 'react';
import { UploadFile, UploadChangeParam } from 'antd/lib/upload/interface';
import { useField } from 'formik';
import { IRequestAttachment } from 'common/schema/request/RequestAttachment';
import {
  IRequestFileValue,
  IRequestFileItem
} from 'common/schema/request/Request';
import { RequestAttachmentApi } from 'client/components/schema/request-attachments/RequestAttachmentApi';
import { IMetadata } from 'common/schema/metadata/Metadata';
import { IWidgetInputValidation } from 'common/schema/widget/input/WidgetInput';
import { WidgetInputFileTypes } from 'common/schema/widget/input/WidgetUploadFileType';

import { debounce } from 'lodash';
import { WidgetInputUploadDraggerText } from '../../helper/WidgetInputUploadDraggerText';
import { WidgetInputType } from 'common/schema/widget/input/WidgetInputType';
import { useCurrentCitizen } from 'client/components/auth/AuthModule';

const B_TO_MB = 1024 * 1024; // Byte in MegaByte
const DEFAULT_ACCEPTED_FILE_TYPES = Object.values<string[]>(
  WidgetInputFileTypes
)
  .map(t => t.join(','))
  .join(',');

export interface IWidgetUploadInputProps {
  name: string;
  description?: string;
  metadata: IMetadata;
  procedureId: number;
  options?: IWidgetInputValidation;
  hint?: React.ReactNode;

  // inserito per gestire testo in caso di Upload documento principale
  widgetType?: WidgetInputType;

  /** Se fornito, visualizza il componente fornito anziché il dragger di default */
  component?: React.ReactNode;
}

/**
 * Ottieniamo l'URL di download degli allegati.
 */
export function getRequestAttachmentUrl(metadataValue: IRequestFileItem) {
  return resolveApiPath(
    `/request-attachments/${metadataValue.attachmentId}/download/${metadataValue.fileName}`
  );
}

/**
 * Converta il valore del metadato (`IRequestFileItem`) in una struttura
 * `UploadFile` che utilizza Ant per il rendering della lista dei documenti.
 *
 * @param canDownload Specifica se è necessario inserire l'URL per il
 * download. Al momento è disabilitato se il cittadino non è autenticato per
 * problemi di sicurezza.
 */
function metadataToUploadFile(
  metadataValue: IRequestFileItem,
  canDownload: boolean
): UploadFile<IRequestFileItem> {
  return {
    uid: String(metadataValue.attachmentId),
    name: metadataValue.fileName,
    url: canDownload ? getRequestAttachmentUrl(metadataValue) : undefined,
    size: metadataValue.size,
    type: metadataValue.type,
    status: 'success' as 'success',
    response: metadataValue
  };
}

function attachmentToFileValue(
  attachment: IRequestAttachment
): IRequestFileItem {
  return {
    attachmentId: attachment.id,
    fileName: attachment.fileName,
    size: attachment.size,
    type: attachment.type
  };
}

function uploadFileToMetadata(file: UploadFile<IRequestFileItem>) {
  return {
    ...file.response!,
    size: file.size,
    type: file.type
  };
}

type IFileList = UploadFile<IRequestFileItem>[];

/**
 * Input dedicato al caricamento dei file da Widget.
 * Si basa sul `MultipleUploadInput` ma è personalizzato.
 */
export function WidgetUploadInput(props: IWidgetUploadInputProps) {
  // Serve per sapere se si è effettuato il login o meno.
  const citizen = useCurrentCitizen();
  // Si può effettuare il download dell'allegato solo se si è autenticati.
  const canDownload = citizen != null;

  const { name, metadata, options, procedureId } = props;
  const [field, , helpers] = useField<IRequestFileValue>(name);
  const [fileList, setFileList] = useState<IFileList>(
    field.value?.map(item => metadataToUploadFile(item, canDownload))
  );
  const multipleFile = options?.multiple ?? false;

  const maxFileSize = options?.maxsize ?? -1;
  const acceptedFileTypes =
    !options?.filetype || options.filetype.length === 0
      ? DEFAULT_ACCEPTED_FILE_TYPES
      : options.filetype.join(',');

  const uploadMessage = useCallback(
    debounce(status => {
      if (status === 'done') {
        message.success('Allegato caricato correttamente');
      } else if (status === 'error') {
        message.error(
          "Si è verificato un errore durante l'upload dell'allegato, si prega di riprovare."
        );
      }
    }, 500),
    []
  );

  const description = props.description;

  const componentProps = {
    fileList,
    accept: acceptedFileTypes,
    multiple: multipleFile,
    onRemove: async (file: UploadFile<IRequestFileItem>) => {
      if (file.response?.attachmentId)
        helpers.setValue(
          field.value?.filter(
            fv => fv.attachmentId !== file.response!.attachmentId
          )
        );
    },
    showUploadList: {
      showDownloadIcon: canDownload,
      showPreviewIcon: false,
      showRemoveIcon: true
    },
    customRequest: (options: any) => {
      const { onSuccess, onError, file } = options;
      const data = new FormData();
      // Solo `append` è IE11 compatible
      data.append('attachment', file);
      data.append('metadataId', metadata.id);
      data.append('procedureId', String(procedureId));
      RequestAttachmentApi.upload({ data })
        .then(res => {
          onSuccess(attachmentToFileValue(res.data), file);
        })
        .catch(err => {
          console.error('[Upload] Error after API', err);
          onError(err);
        });
    },
    onChange: (info: UploadChangeParam<UploadFile<IRequestFileItem>>) => {
      let fileList: UploadFile<IRequestFileItem>[] = info.fileList.map(file => {
        // Impostiamo l'URL perché Ant non lo calcola nativamente
        // sull'oggetto `UploadFile`
        if (canDownload && file.response?.attachmentId) {
          file.url = getRequestAttachmentUrl(file.response);
        }

        return file;
      });

      // Se non è permessa la selezione di file multipli, mantieni solo l'ultimo.
      if (!multipleFile) fileList = fileList.slice(-1);
      fileList = fileList.map(f => {
        return { ...f, status: f.status ?? 'error' };
      });
      const { status } = info.file;

      uploadMessage(status);

      helpers.setValue([
        ...fileList.filter(f => f.status !== 'error').map(uploadFileToMetadata)
      ]);
      setFileList([...fileList.filter(f => f.status !== 'error')]);
    },
    beforeUpload: (file: any) => {
      // SIZE CHECK
      if (maxFileSize > 0 && file.size / B_TO_MB > maxFileSize) {
        const fileSize = Math.ceil((file.size / B_TO_MB) * 100) / 100;
        message.error(
          `Il file ${file.name} è troppo grande (${fileSize} MB). Dimensione massima: ${maxFileSize} MB`
        );
        return false;
      }
      // TYPE CHECK
      if (!acceptedFileTypes.split(',').includes(file.type)) {
        message.error(`Il formato del file ${file.name} non è ammesso.`);
        return false;
      }
      return true;
    }
  };

  if (props.component) {
    return <Upload {...componentProps}>{props.component}</Upload>;
  }

  return (
    <Dragger {...componentProps}>
      <WidgetInputUploadDraggerText
        widgetType={props.widgetType}
        description={description}
        hint={props.hint}
      />
    </Dragger>
  );
}
